import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  REALTIME_CLOSE, REALTIME_MAX_CHANNELS, REALTIME_SILENCE_MS,
  parseChannel, platformChannel, tenantChannel,
  type LiveCallsData, type LiveQueuesData, type RealtimeClientMessage,
  type RealtimeServerMessage, type RealtimeTicket,
} from '@arcnet/contracts';
import { api, onAuthChange } from './client.js';

/**
 * Live updates from the control plane: one WebSocket per tab (doc 05 §8).
 *
 * Event-driven end to end (owner, 15 September 2026). The switch raises an
 * event, arc-agent pushes, arc-api fans it out on channels, and this renders
 * it. Nothing here asks "anything new?" on a timer.
 *
 * ONE socket for the tab, however many hooks want it. Channels are refcounted
 * across hooks and the subscribe/unsubscribe traffic is settled in a microtask,
 * so StrictMode's mount-unmount-mount and a page handing over to the next one
 * in the same commit send nothing at all. The socket lingers a few seconds
 * after the last hook leaves for the same reason.
 *
 * Auth is a single-use ticket minted over REST, because a browser cannot put an
 * Authorization header on a WebSocket and a bearer token in a URL outlives the
 * request in somebody's log. Every connect mints a fresh one.
 *
 * Registrations are still NUDGES: the channel says presence changed and the
 * hook refetches /v1/live/presence, so registration rows keep one code path and
 * one permission. Calls and queues are payloads -- see useLiveState.
 */

export type RealtimeState = 'connecting' | 'live' | 'offline';

/** What a hook hears on one of its channels. */
export type ChannelMessage =
  | { type: 'snapshot' | 'update'; channel: string; at: string; data: LiveCallsData | LiveQueuesData }
  | { type: 'nudge'; channel: string; at: string }
  | { type: 'error'; channel: string; code: string; message: string };
type ChannelListener = (msg: ChannelMessage) => void;

const RETRY_MIN_MS = 1_000;
const RETRY_MAX_MS = 30_000;
/** Long enough to cover a navigation between two live pages. */
const LINGER_MS = 10_000;

// ------------------------------------------------------------ the connection

/** Channel -> the hooks listening on it. The keys are what the socket should hold. */
const listeners = new Map<string, Set<ChannelListener>>();
/** Channels the current socket has asked for. */
const held = new Set<string>();
/** Last payload per channel, so a hook joining a channel already held renders at once. */
const latest = new Map<string, ChannelMessage>();
/** Registrations channels that missed nudges while the socket was down. */
const resync = new Set<string>();

let socket: WebSocket | null = null;
let ready = false;
/** A ticket is being minted. */
let connecting = false;
/** Bumped by teardown so a mint still in flight knows it is no longer wanted. */
let generation = 0;
let attempt = 0;
/** 4003: the Origin was refused, and asking again cannot change that. */
let stopped = false;
let installed = false;
let flushQueued = false;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let lingerTimer: ReturnType<typeof setTimeout> | null = null;
let silenceTimer: ReturnType<typeof setTimeout> | null = null;

let state: RealtimeState = 'connecting';
const stateListeners = new Set<() => void>();
function setState(next: RealtimeState): void {
  if (state === next) return;
  state = next;
  stateListeners.forEach((l) => l());
}

function install(): void {
  if (installed) return;
  installed = true;
  onAuthChange((authenticated) => {
    // Fires on every token refresh too, which is a no-op for a live socket.
    if (authenticated) { ensureConnected(); return; }
    // Signed out: the next person to sign in on this tab must not be shown
    // the last one's calls from the cache.
    teardown();
    latest.clear();
  });
  window.addEventListener('online', () => {
    if (!retryTimer || listeners.size === 0) return;
    clearTimeout(retryTimer);
    retryTimer = null;
    void connect();
  });
}

function ensureConnected(): void {
  if (lingerTimer) { clearTimeout(lingerTimer); lingerTimer = null; }
  if (stopped || socket || connecting || retryTimer || listeners.size === 0) return;
  void connect();
}

async function connect(): Promise<void> {
  const gen = ++generation;
  connecting = true;
  if (state !== 'offline') setState('connecting');

  let ticket: string;
  try {
    ticket = (await api.post<RealtimeTicket>('/v1/realtime/ticket')).ticket;
  } catch {
    if (gen !== generation) return;
    connecting = false;
    retry();
    return;
  }
  if (gen !== generation) return;
  connecting = false;
  if (listeners.size === 0) { setState('connecting'); return; }

  const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
  let ws: WebSocket;
  try {
    ws = new WebSocket(`${scheme}://${location.host}/api/v1/stream?ticket=${encodeURIComponent(ticket)}`);
  } catch {
    retry();
    return;
  }
  socket = ws;
  armSilence();
  ws.onmessage = (ev: MessageEvent) => { if (socket === ws) receive(ev); };
  // An error is always followed by a close, which is where it is handled.
  ws.onclose = (ev: CloseEvent) => { if (socket === ws) closed(ev.code); };
}

function receive(ev: MessageEvent): void {
  armSilence();
  let msg: RealtimeServerMessage;
  try { msg = JSON.parse(String(ev.data)) as RealtimeServerMessage; }
  catch { return; }   // one frame we cannot read is one frame, not a failure

  switch (msg.type) {
    case 'ready':
      ready = true;
      attempt = 0;
      setState('live');
      flush();
      return;
    case 'snapshot':
    case 'update':
      latest.set(msg.channel, msg);
      dispatch(msg.channel, msg);
      return;
    case 'nudge':
      dispatch(msg.channel, msg);
      return;
    case 'subscribed':
      for (const channel of msg.channels) {
        if (resync.delete(channel)) dispatch(channel, { type: 'nudge', channel, at: new Date().toISOString() });
      }
      return;
    case 'error':
      // A refused channel stays in `held`: asking again on this socket would
      // be refused again. The next connection tries it afresh.
      if (msg.channel) {
        dispatch(msg.channel, { type: 'error', channel: msg.channel, code: msg.code, message: msg.message });
      }
      return;
    default:
      return;   // heartbeat: re-arming the silence timer was the whole point
  }
}

function dispatch(channel: string, msg: ChannelMessage): void {
  for (const listener of [...(listeners.get(channel) ?? [])]) {
    // One page throwing must not stop the next one hearing the update.
    try { listener(msg); } catch (err) { reportError(err); }
  }
}

function closed(code: number): void {
  for (const channel of held) {
    if (parseChannel(channel)?.topic === 'registrations') resync.add(channel);
  }
  dropSocket();
  if (code === REALTIME_CLOSE.originRefused) {
    stopped = true;
    setState('offline');
    return;
  }
  // 4002 on a socket that was live is most often the access token its ticket
  // was minted with running out, once a token lifetime. Minting refreshes the
  // token, so straight back rather than a backoff that shows the page offline.
  // A second 4002 before `ready` backs off like any other close.
  if (code === REALTIME_CLOSE.sessionEnded && attempt === 0 && listeners.size > 0) {
    attempt = 1;
    void connect();
    return;
  }
  // 4001 and 4002 need a new ticket, 4008 a fresh snapshot, and anything else
  // a new connection. Every connect mints a ticket and every subscribe brings a
  // snapshot, so all of them are the same thing: try again.
  retry();
}

function retry(): void {
  if (listeners.size === 0) { setState('connecting'); return; }
  setState('offline');
  const spread = Math.min(RETRY_MAX_MS - RETRY_MIN_MS, RETRY_MIN_MS * 2 ** attempt);
  attempt = Math.min(attempt + 1, 10);
  // Jitter above the floor, first retry included, so every tab that lost the
  // same API restart does not return on the same tick.
  const delay = RETRY_MIN_MS + Math.random() * spread;
  retryTimer = setTimeout(() => { retryTimer = null; ensureConnected(); }, delay);
}

/**
 * The server speaks at least every REALTIME_HEARTBEAT_MS. Hearing nothing for
 * longer than that is a connection that died without a close -- a laptop lid,
 * a NAT that forgot us -- and would otherwise sit "live" forever.
 */
function armSilence(): void {
  if (silenceTimer) clearTimeout(silenceTimer);
  const ws = socket;
  silenceTimer = setTimeout(() => { if (ws && socket === ws) closed(1006); }, REALTIME_SILENCE_MS);
}

function dropSocket(): void {
  const ws = socket;
  socket = null;
  ready = false;
  held.clear();
  if (silenceTimer) { clearTimeout(silenceTimer); silenceTimer = null; }
  if (!ws) return;
  ws.onmessage = null;
  ws.onclose = null;
  if (ws.readyState === WebSocket.CONNECTING || ws.readyState === WebSocket.OPEN) {
    try { ws.close(1000); } catch { /* already going */ }
  }
}

function teardown(): void {
  generation += 1;
  connecting = false;
  attempt = 0;
  if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
  if (lingerTimer) { clearTimeout(lingerTimer); lingerTimer = null; }
  dropSocket();
  resync.clear();
  setState('connecting');
}

/** Settles what the socket holds against what the hooks want, once per tick. */
function scheduleFlush(): void {
  if (flushQueued) return;
  flushQueued = true;
  queueMicrotask(() => { flushQueued = false; flush(); });
}

function flush(): void {
  for (const channel of [...latest.keys()]) {
    if (!listeners.has(channel)) latest.delete(channel);
  }
  if (!socket || !ready || socket.readyState !== WebSocket.OPEN) return;
  const add = [...listeners.keys()].filter((c) => !held.has(c));
  const drop = [...held].filter((c) => !listeners.has(c));
  add.forEach((c) => held.add(c));
  drop.forEach((c) => held.delete(c));
  send('subscribe', add);
  send('unsubscribe', drop);
}

function send(type: RealtimeClientMessage['type'], channels: string[]): void {
  for (let i = 0; i < channels.length; i += REALTIME_MAX_CHANNELS) {
    const msg: RealtimeClientMessage = { type, channels: channels.slice(i, i + REALTIME_MAX_CHANNELS) };
    socket?.send(JSON.stringify(msg));
  }
}

function join(channel: string, listener: ChannelListener): () => void {
  install();
  let set = listeners.get(channel);
  if (!set) { set = new Set(); listeners.set(channel, set); }
  set.add(listener);
  const last = latest.get(channel);
  if (last) listener(last);
  scheduleFlush();
  ensureConnected();

  return () => {
    const s = listeners.get(channel);
    if (!s?.delete(listener)) return;
    if (s.size === 0) listeners.delete(channel);
    scheduleFlush();
    if (listeners.size === 0 && !lingerTimer) {
      lingerTimer = setTimeout(() => {
        lingerTimer = null;
        if (listeners.size === 0) teardown();
      }, LINGER_MS);
    }
  };
}

const subscribeState = (fn: () => void): (() => void) => {
  stateListeners.add(fn);
  return () => { stateListeners.delete(fn); };
};
const getState = (): RealtimeState => state;

// ------------------------------------------------------------------- hooks

/**
 * Listens on `channels` for as long as the component is mounted.
 *
 * The handler is held in a ref, so a new callback identity each render does not
 * resubscribe; only a change in the channel names does. A name that is not a
 * channel is dropped here: the server validates a subscribe as a whole, and one
 * bad name would lose the good ones sent beside it.
 */
export function useChannels(channels: readonly string[], onMessage: ChannelListener): RealtimeState {
  const handler = useRef(onMessage);
  handler.current = onMessage;
  const key = channels.filter((c) => parseChannel(c) !== null).join(' ');

  useEffect(() => {
    if (!key) return;
    const listener: ChannelListener = (msg) => handler.current(msg);
    const leave = key.split(' ').map((channel) => join(channel, listener));
    return () => leave.forEach((l) => l());
  }, [key]);

  return useSyncExternalStore(subscribeState, getState, getState);
}

interface PresenceEntry {
  tenant_id: string; realm: string; number: string; registered: boolean;
  contact_ip: string | null; contact_port: number | null;
  transport: string | null; user_agent: string | null; ping_status: string | null;
  expires_at: string | null; last_seen_at: string | null;
  offline_since: string | null; first_seen_at: string | null; snapshot_at: string | null;
}

/**
 * Merges fresh presence into whatever the cache already holds.
 *
 * Deliberately a no-op on anything not already cached: this is an update, not a
 * fetch. A page that has never loaded its extensions has nothing to recolour,
 * and making this populate the cache would fire off list-shaped writes from a
 * presence-shaped response.
 */
async function applyPresence(qc: QueryClient, tenantId: string | null): Promise<void> {
  let entries: PresenceEntry[];
  try {
    const res = await api.get<{ available: boolean; data: PresenceEntry[] }>(
      tenantId ? `/v1/live/presence?tenant_id=${encodeURIComponent(tenantId)}` : '/v1/live/presence');
    // available:false means the API has no node beside it. Leaving the cache
    // untouched shows the last known state, which beats blanking every dot.
    if (!res.available) return;
    entries = res.data;
  } catch {
    // A failed presence fetch must not disturb the page. The next event, or the
    // agent's 30-second sweep, tries again.
    return;
  }

  const byNumber = new Map(entries.map((e) => [`${e.tenant_id}|${e.number}`, e]));

  /** The registration sub-object the extension DTO carries. */
  const toRegistration = (e: PresenceEntry | undefined) => (e === undefined ? undefined : {
    registered: e.registered,
    contact: null as string | null,
    contact_ip: e.contact_ip,
    contact_port: e.contact_port,
    transport: e.transport,
    user_agent: e.user_agent,
    ping_status: e.ping_status,
    expires_at: e.expires_at,
    last_seen_at: e.last_seen_at,
    offline_since: e.offline_since,
    first_seen_at: e.first_seen_at,
    snapshot_at: e.snapshot_at,
  });

  // Tenant-scoped list: { data: Extension[] }
  qc.setQueriesData<{ data: Array<Record<string, unknown>> }>(
    { queryKey: ['extensions'] },
    (old) => {
      if (!old?.data) return old;
      return {
        ...old,
        data: old.data.map((x) => {
          const e = byNumber.get(`${String(x['tenant_id'])}|${String(x['number'])}`);
          const reg = toRegistration(e);
          // `contact` is not carried by the presence endpoint; keep whatever the
          // last full fetch had rather than blanking a field we did not ask for.
          return reg
            ? { ...x, registration: { ...reg, contact:
                (x['registration'] as { contact?: string | null } | undefined)?.contact ?? null } }
            : x;
        }),
      };
    });

  // A single extension: the detail page.
  qc.setQueriesData<Record<string, unknown>>(
    { queryKey: ['extension'] },
    (old) => {
      if (!old || typeof old['number'] !== 'string') return old;
      const e = byNumber.get(`${String(old['tenant_id'])}|${String(old['number'])}`);
      const reg = toRegistration(e);
      return reg
        ? { ...old, registration: { ...reg, contact:
            (old['registration'] as { contact?: string | null } | undefined)?.contact ?? null } }
        : old;
    });

  // The operator's cross-tenant view, which is flat rather than nested.
  qc.setQueriesData<{ data: Array<Record<string, unknown>> }>(
    { queryKey: ['live-extensions'] },
    (old) => {
      if (!old?.data) return old;
      const merged: Array<Record<string, unknown>> = old.data.map((x) => {
        const e = byNumber.get(`${String(x['tenant_id'])}|${String(x['number'])}`);
        if (!e) return { ...x, registered: false };
        return {
          ...x,
          registered: e.registered,
          contact_ip: e.contact_ip, contact_port: e.contact_port,
          transport: e.transport, user_agent: e.user_agent,
          last_seen_at: e.last_seen_at, offline_since: e.offline_since,
          ever_seen: true,
        };
      });
      // Same order the server applies, so a row does not jump only because the
      // update arrived by a different route than the last one.
      merged.sort((a, b) =>
        Number(b['registered']) - Number(a['registered'])
        || String(a['tenant_name']).localeCompare(String(b['tenant_name']))
        || String(a['number']).localeCompare(String(b['number']), undefined, { numeric: true }));
      return { ...old, data: merged };
    });
}

/**
 * One presence fetch per scope at a time.
 *
 * A list page and the extension open beside it both hear the same nudge, and a
 * registration storm sends several a second. Nudges that land mid-fetch collapse
 * into one more fetch after it, so the last change is never missed.
 */
const presenceRuns = new Map<string, 'running' | 'again'>();
function requestPresence(qc: QueryClient, tenantId: string | null): void {
  const key = tenantId ?? '*';
  if (presenceRuns.has(key)) { presenceRuns.set(key, 'again'); return; }
  presenceRuns.set(key, 'running');
  void applyPresence(qc, tenantId).finally(() => {
    const again = presenceRuns.get(key) === 'again';
    presenceRuns.delete(key);
    if (again) requestPresence(qc, tenantId);
  });
}

/**
 * Registrations for one tenant, or `platform` for every tenant at once.
 *
 * The platform channel exists so an operator with thirty accounts holds one
 * subscription rather than thirty, and need not reopen the set whenever an
 * account is created. The API refuses it to anyone whose scope is not actually
 * the whole platform.
 */
export function useRealtime(
  tenantId: string | null,
  opts: { platform?: boolean } = {},
): RealtimeState {
  const qc = useQueryClient();
  const platform = opts.platform === true;
  const channel = platform
    ? platformChannel('registrations')
    : tenantId ? tenantChannel(tenantId, 'registrations') : null;

  return useChannels(channel ? [channel] : [], (msg) => {
    /**
     * Fetch presence, not the whole extension list.
     *
     * Invalidating meant every registration cost a 1128 ms refetch of forty
     * fields per extension to recolour one dot. Presence lives on the node and
     * has its own endpoint that costs single-digit milliseconds. The nudge
     * carries no data, so the extension's own fields still have one writer.
     */
    if (msg.type === 'nudge') requestPresence(qc, platform ? null : tenantId);
  });
}
